<script setup>
import { ref, inject, computed, watch } from 'vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { Listbox, ListboxButton, ListboxLabel, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { FEEDBACK_TABS, FEEDBACK_REPORT_AREAS } from '@/constants'
import { useGlobalStore } from '@/stores/GlobalStore'

const dialog = inject('showFeedbackDialog')

const globalStore = useGlobalStore()
const selectedTab = ref(dialog.value.tab || FEEDBACK_TABS[0])

const selectedArea = ref('')
const selectedFiles = ref([])
const feedbackMsg = ref('')

const step = ref(0)

const dropping = ref(false)

const sendFeedbackLoader = computed(() => globalStore.sendFeedbackLoader)
const sendFeedbackError = computed(() => globalStore.sendFeedbackError)

const processFiles = async (files) => {
  for (let i = 0; i < files.length; i++) {
    let upload = { 
      file: files[i],
      id: i,
      thumbnail: null,
    }

    const createThumbnail = (file) => {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader()

        fileReader.readAsDataURL(file)

        fileReader.onload = (event) => {
          upload['thumbnail'] = event.target.result
          resolve(fileReader.result)
        }

        fileReader.onerror = (error) => {
          reject(error)
        }
      })
    }

    await createThumbnail(files[i], 'thumbnail')
    selectedFiles.value.push(upload)
  }
}

const onSelectFiles = (e) => {
  if (e.target.files.length) {
    processFiles(e.target.files)
  }
}

const closeDialog = () => {
  dialog.value = { ...dialog.value, open: false, tab: FEEDBACK_TABS[0] }
  step.value = 0
  selectedFiles.value = []
  setTimeout(() => {
    selectedTab.value = FEEDBACK_TABS[0]
    selectedArea.value = ''
    feedbackMsg.value = ''
  }, 300);
}

const handleSubmit = () => {
  let formData = new FormData()

  for(let i = 0; i < selectedFiles.value.length; i++) {
    formData.append(`files[${i}]`, selectedFiles.value[i].file)
  }

  formData.append(`type`, selectedTab.value.type)
  formData.append(`area`, selectedArea.value)
  formData.append(`message`, feedbackMsg.value)

  globalStore.postFeedback(formData)
}

const getDisabled = () => {
  return !selectedArea.value || !feedbackMsg.value
}

const removeImage = (imageId) => {
  selectedFiles.value = selectedFiles.value.reduce((acc, item) => {
    if (item.id !== imageId) {
      acc.push(item)
    }

    return acc
  }, [])
}

watch(sendFeedbackLoader, () => {
  if (!sendFeedbackLoader.value && !sendFeedbackError.value) {
    step.value = 1
  }
})

watch(dialog, () => {
  selectedTab.value = dialog.value.tab || FEEDBACK_TABS[0]
})
</script>

<template>
  <section>
    <TransitionRoot as="template" :show="dialog.open">
      <Dialog as="div" class="relative z-[9999]" @close="closeDialog">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black bg-opacity-[0.38] transition-opacity backdrop-blur-sm" />
        </TransitionChild>
    
        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full w-full justify-center md:items-center md:p-[30px]">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
              enter-to="opacity-100 translate-y-0 md:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 md:scale-100"
              leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            >
              <DialogPanel
                class="relative rounded-lg bg-white p-6 pt-0 shadow-xl transition-all w-full max-w-xl flex-1 text-sm"
              >
                <section>
                  <header class="flex items-center justify-between border-b py-4">
                    <DialogTitle class="text-lg font-semibold">
                      <template v-if="selectedTab.type === 'question'">Ask a question</template>
                      <template v-else-if="selectedTab.type === 'feedback'">Give feedback</template>
                      <template v-else-if="selectedTab.type === 'bug'">Report a bug</template>
                    </DialogTitle>
                    <button @click="closeDialog">
                      <Icon icon="heroicons:x-mark" class="text-xl" />  
                    </button>
                  </header>

                  <template v-if="step === 0 || sendFeedbackError">
                    <section class="py-6 flex flex-col gap-y-4">
                      <section>
                        <h4 class="text-xs font-medium mb-4">What type of message is this?</h4>
                        <ul class="flex items-stretch divide-x divide-gray-300 rounded-lg overflow-hidden border border-gray-300">
                          <template v-for="tab, index in FEEDBACK_TABS" :key="index">
                            <li
                              class="flex items-center justify-center gap-x-2 font-medium p-4 flex-1 transition duration-300 cursor-pointer"
                              @click="selectedTab = tab"
                              :class="selectedTab.text === tab.text ? 'bg-tint-1 text-white' : 'text-tint-1'"
                            >
                              <Icon :icon="tab.icon" class="text-2xl" /> {{ tab.text }}
                            </li>
                          </template>
                        </ul>
                      </section>
  
                      <Listbox v-model="selectedArea" v-slot="{ open }">
                        <div class="relative z-[20]">
                          <ListboxLabel class="controller-label text-xs">What area does this best relate to?</ListboxLabel>
                          <ListboxButton
                            class="
                              relative w-full cursor-default rounded-lg bg-gray-50 py-2 px-4 text-left border flex items-center
                              gap-x-2 text-sm transition duration-300 focus:outline-none focus-visible:border-tint-1
                              focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-50 focus-visible:ring-offset-2
                              focus-visible:ring-offset-blue-300
                            "
                            :class="{ 'border-tint-1': open }"
                          >
                            <span
                              v-text="selectedArea || 'Select a value'"
                              class="transition duration-300 flex-1"
                              :class="{ 'text-gray-500': !selectedArea }"
                            />
                            <Icon
                              icon="heroicons:chevron-down-16-solid"
                              class="text-base text-gray-500 pointer-events-none transition duration-300"
                              :class="{ 'rotate-180': open }"
                            />
                          </ListboxButton>
            
                          <transition name="fade">
                            <ListboxOptions
                              class="
                                absolute right-0 top-full mt-1 max-h-96 min-w-[10rem] w-full overflow-auto rounded-lg bg-white py-2
                                text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none
                              "
                            >
                              <ListboxOption
                                v-slot="{ active, selected }"
                                v-for="(option, idx) in FEEDBACK_REPORT_AREAS"
                                :value="option"
                                :key="idx"
                                as="template"
                              >
                                <li
                                  class="
                                    cursor-pointer select-none py-2 pl-3 pr-3 flex items-center justify-between transition duration-300
                                  "
                                  :class="{ 'bg-gray-100': active }"
                                >
                                  <span
                                    class="block truncate"
                                    :class="{ 'font-medium text-tint-1' : selected }"
                                    v-text="option"
                                  />
                                  <Icon
                                    icon="heroicons:check-20-solid"
                                    class="h-5 w-5 text-tint-1"
                                    aria-hidden="true"
                                    v-if="selected"
                                  />
                                </li>
                              </ListboxOption>
                            </ListboxOptions>
                          </transition>
                        </div>
                      </Listbox>
  
                      <label class="block">
                        <span class="controller-label text-xs">
                          <template v-if="selectedTab.text === 'Question'">What can we help you with?</template>
                          <template v-else-if="selectedTab.text === 'Feedback'">What feedback do you have?</template>
                          <template v-else-if="selectedTab.text === 'Bug'">What’s the bug?</template>
                        </span>
                        <textarea
                          class="input-controller font-normal px-5 h-28"
                          placeholder="Type your message here"
                          v-model="feedbackMsg"
                        />
                      </label>
  
                      <label class="select-none relative">
                        <div :class="{'flex justify-between items-center': selectedFiles.length}">
                          <span class="controller-label text-xs" :class="{'!m-0': selectedFiles.length}">Attach Images</span>
                          <template v-if="!selectedFiles.length">
                            <div
                              class="
                                border border-dashed border-gray-400 rounded-lg flex flex-1 h-[100px] items-center gap-x-2 justify-center text-gray-500
                                cursor-pointer
                              "
                              :class="{ '!border-tint-1 !bg-blue-50': dropping }"
                            >
                              <Icon icon="heroicons:folder-open" class="text-2xl" />
                              <p>Drop files here or click to upload</p>
                            </div>
                          </template>
                          <template v-else>
                            <button class="flex items-center gap-x-2 text-sm font-medium leading-5 text-tint-1">
                              <Icon icon="heroicons:plus-16-solid"/> Add Image
                            </button>
                          </template>
                        </div>
                       
                        <input
                          type="file"
                          name="UploadInput"
                          multiple="true"
                          id="searchImage"
                          class="absolute left-0 top-0 opacity-0 w-full h-full outline-none cursor-pointer z-10"
                          accept="image/jpeg,image/png,image/tiff"
                          @change.stop.prevent="onSelectFiles"
                        />
                      </label>
                      <div v-if="selectedFiles.length" class="w-full overflow-y-auto h-[160px]">
                        <div class="inline-flex gap-x-2">
                          <template v-for="image, index in selectedFiles" :key="index">
                            <div class="relative w-[120px] h-[84px]">
                              <img :src="image.thumbnail"  class="object-contain" />
                              <button @click="() => removeImage(image.id)" class="w-4 h-4 border-gray-200 bg-white border rounded-[50%] absolute top-0 right-0 cursor-pointer">
                                <Icon icon="heroicons:x-mark" class="text-sm" />  
                              </button>
                            </div>
                          </template>
                        </div>
                      </div>
                    </section>
                  </template>
                  <template v-if="step === 1 && !sendFeedbackError">
                    <section class="flex justify-center pt-[115px] pb-[195px]">
                      <div class="gap-y-6 pt-10 flex flex-col items-center">
                        <img src="@/assets/images/feedback-success.png" class="w-[104px] h-[120px]" />
                        <span class="text-base font-medium text-gray-900">Your feedback has been submitted.</span>
                      </div>

                    </section>
                  </template>
                 
                </section>

                <footer class="pt-6 border-t flex items-stretch justify-end gap-x-4" :class="{'justify-center': step === 1 && !sendFeedbackError}">
                  <template v-if="step === 0 || sendFeedbackError">
                    <button class="alternative-button" @click="closeDialog">Cancel</button>
                    <button class="primary-button" :disabled="getDisabled()" @click="handleSubmit">Submit</button>
                  </template>
                  <template v-if="step === 1 && !sendFeedbackError">
                    <button class="primary-button" @click="closeDialog">Done</button>
                  </template>
                </footer>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </section>
</template>